import {makeAutoObservable} from 'mobx';
import {safeConsoleError} from '../../../../lib/sanitize';

/**
 * SSE Connection state for tracking individual processing details streams
 */
export type SSEConnectionState = {
    state: 'connecting' | 'connected';
    isClosed: boolean;
    hasReceivedFinalStatus: boolean;
    abortController: AbortController | null;
    seenEventIds: Set<number>;
    streamId: string | null;
};

export class SSEConnectionStore {
    private _processingDetailsSSEConnections: Map<number, SSEConnectionState> = new Map();
    private _activeStreamIds: Set<string> = new Set();
    private _reconnectedStreams: Set<string> = new Set();

    constructor() {
        makeAutoObservable(this);
    }

    get processingDetailsSSEConnections(): Map<number, SSEConnectionState> {
        return this._processingDetailsSSEConnections;
    }

    get activeStreamIds(): Set<string> {
        return this._activeStreamIds;
    }

    get hasActiveConnections(): boolean {
        return this._processingDetailsSSEConnections.size > 0;
    }

    /**
     * Create and initialize SSE connection state for a file
     * Prevents duplicate connections if one already exists for the fileId
     * @param fileId - The file ID to connect
     * @param fileName - The name of the file (for reference)
     * @param existingEvents - Optional array of existing events to seed seenEventIds
     */
    async connectToProcessingDetailsStream(
        fileId: number,
        fileName: string,
        existingEvents?: any[]
    ): Promise<void> {
        // Prevent duplicate connections
        if (this._processingDetailsSSEConnections.has(fileId)) {
            return;
        }

        // Initialize seenEventIds from existing events
        const seenEventIds = new Set<number>();
        if (existingEvents && Array.isArray(existingEvents)) {
            existingEvents.forEach((event) => {
                if (event.id !== undefined && event.id !== null) {
                    seenEventIds.add(Number(event.id));
                }
            });
        }

        // Create initial connection state
        const connectionState: SSEConnectionState = {
            state: 'connecting',
            isClosed: false,
            hasReceivedFinalStatus: false,
            abortController: null,
            seenEventIds,
            streamId: null,
        };

        this._processingDetailsSSEConnections.set(fileId, connectionState);
    }

    /**
     * Close and remove an SSE connection for a file
     * @param fileId - The file ID to disconnect
     */
    disconnectProcessingDetailsStream(fileId: number): void {
        const connection = this._processingDetailsSSEConnections.get(fileId);
        if (!connection) {
            return;
        }

        // Close connection
        connection.isClosed = true;

        // Abort controller if present
        if (connection.abortController) {
            try {
                connection.abortController.abort();
            } catch (error) {
                // Log error but continue cleanup
                safeConsoleError(`Error aborting controller for file ${fileId}:`, error);
            }
        }

        // Remove from active stream IDs
        if (connection.streamId) {
            this._activeStreamIds.delete(connection.streamId);
        }

        // Remove from map
        this._processingDetailsSSEConnections.delete(fileId);
    }

    /**
     * Close all SSE connections and clear state
     */
    disconnectAllProcessingDetailsStreams(): void {
        const fileIds = Array.from(this._processingDetailsSSEConnections.keys());

        fileIds.forEach((fileId) => {
            this.disconnectProcessingDetailsStream(fileId);
        });

        this._activeStreamIds.clear();
    }

    /**
     * Mark a stream as having been reconnected
     * @param streamId - The stream ID to mark as reconnected
     */
    markStreamAsReconnected(streamId: string): void {
        this._reconnectedStreams.add(streamId);
    }

    /**
     * Check if a stream has been marked as reconnected
     * @param streamId - The stream ID to check
     * @returns true if the stream is marked as reconnected
     */
    isStreamReconnected(streamId: string): boolean {
        return this._reconnectedStreams.has(streamId);
    }

    /**
     * Clear all reconnected stream markers
     */
    clearReconnectedStreams(): void {
        this._reconnectedStreams.clear();
    }

    /**
     * Add a stream ID to the active set
     * @param streamId - The stream ID to add
     */
    addActiveStreamId(streamId: string): void {
        this._activeStreamIds.add(streamId);
    }

    /**
     * Remove a stream ID from the active set
     * @param streamId - The stream ID to remove
     */
    removeActiveStreamId(streamId: string): void {
        this._activeStreamIds.delete(streamId);
    }

    /**
     * Check if a stream ID is in the active set
     * @param streamId - The stream ID to check
     * @returns true if the stream ID is active
     */
    hasActiveStreamId(streamId: string): boolean {
        return this._activeStreamIds.has(streamId);
    }

    /**
     * Update specific properties of a connection state
     * @param fileId - The file ID whose connection to update
     * @param updates - Partial connection state properties to update
     */
    updateConnectionState(fileId: number, updates: Partial<SSEConnectionState>): void {
        const connection = this._processingDetailsSSEConnections.get(fileId);
        if (!connection) {
            return;
        }

        Object.assign(connection, updates);
    }
}
